import React from "react";
import { X, LogIn, LogOut, X as CancelIcon, AlertTriangle } from "lucide-react";

const ConfirmationModal = ({ isOpen, onClose, onConfirm, action, booking, isProcessing = false }) => {
  if (!isOpen || !booking) return null;

  const getActionConfig = () => {
    switch (action) {
      case "check-in":
        return {
          title: "Check In Guest",
          description: `Are you sure you want to check in ${booking.name}?`,
          icon: <LogIn className="w-6 h-6 text-green-600" />,
          buttonText: "Check In",
          buttonColor: "bg-green-600 hover:bg-green-700",
          iconColor: "text-green-600",
        };
      case "check-out":
        return {
          title: "Check Out Guest",
          description: `Are you sure you want to check out ${booking.name}?`,
          icon: <LogOut className="w-6 h-6 text-blue-600" />,
          buttonText: "Check Out",
          buttonColor: "bg-blue-600 hover:bg-blue-700",
          iconColor: "text-blue-600",
        };
      case "cancel":
        return {
          title: "Cancel Booking",
          description: `Are you sure you want to cancel this booking for ${booking.name}?`,
          icon: <CancelIcon className="w-6 h-6 text-red-600" />,
          buttonText: "Cancel Booking",
          buttonColor: "bg-red-600 hover:bg-red-700",
          iconColor: "text-red-600",
        };
      default:
        return {
          title: "Confirm Action",
          description: "Are you sure you want to proceed?",
          icon: <AlertTriangle className="w-6 h-6 text-yellow-600" />,
          buttonText: "Confirm",
          buttonColor: "bg-indigo-600 hover:bg-indigo-700",
          iconColor: "text-yellow-600",
        };
    }
  };

  const config = getActionConfig();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="p-6">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-6">
              <div
                className={`w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center ${config.iconColor}`}
              >
                {config.icon}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{config.title}</h2>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-600">{config.description}</p>

              {/* Booking Info */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="overflow-hidden flex-shrink-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                    <img
                      src={booking.avatar}
                      alt={booking.name}
                      className="object-cover w-full h-full rounded-full"
                      onError={e => {
                        e.target.style.display = "none";
                        e.target.parentNode.innerHTML =
                          '<div class="flex justify-center items-center w-full h-full text-xs font-medium text-white">' +
                          booking.name
                            .split(" ")
                            .map(n => n[0])
                            .join("") +
                          "</div>";
                      }}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{booking.name}</div>
                    <div className="text-sm text-gray-600">{booking.email}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 ${config.buttonColor}`}
              >
                {isProcessing ? "Processing..." : config.buttonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
